import { ClipboardEvent as ReactClipboardEvent, DragEvent, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CasRHFMultilineTextField } from 'src/compass/shared/components';
import {
  capturePastedPictures,
  hasPastedText,
  isPictureFile,
  picturesFromFiles,
  reportUnpasted,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/utils/clipboardPictures';

import { MAX_SMS_TEXT_LENGTH } from './composeMode';

interface Props {
  name?: string;
  /*
    The form field a pasted or dropped picture is added to, as a data: URI - the text's picture
    attachments. Without it the box takes words only, as a plain text box would.
  */
  picturesName?: string;
}

/*
  A text message's plain-text body, with a live count against the carrier's length cap.

  Shared by the compose window and Batch Send so both show the limit the server enforces
  (SmsService.MaxTextLength). The count turns red past the cap. The compose window's schema also
  blocks the send; Batch Send checks on Send instead, once it knows which rows are texts - its text
  body may be used by none of them.

  A picture pasted or dropped here becomes one of the text's attachments: a text box has nowhere to
  put a picture, and a sender working on the text should not have to go back to the email body to
  add one. It is sized to send on the way in, exactly as one pasted into the Body is. So does a
  picture pasted anywhere else in the window while this box is on screen - see onDocumentPaste.
*/

// Laid out on screen: false under a display:none, such as a view or pane that is not showing.
const isShown = (element: Element) => element.getClientRects().length > 0;

const isShownField = (element: Element) =>
  (element.matches('input, textarea, select') || !!element.closest('[contenteditable="true"]')) &&
  isShown(element);

export default function SmsTextField({ name = 'textContent', picturesName }: Props) {
  const { control, getValues, setValue } = useFormContext();
  const text: string = useWatch({ control, name }) ?? '';
  const boxRef = useRef<HTMLDivElement>(null);

  const over = text.length > MAX_SMS_TEXT_LENGTH;
  const count = `${text.length} / ${MAX_SMS_TEXT_LENGTH} characters`;

  // Read at the moment they land, not from a render: pictures arrive a moment after the paste.
  const addPictures = (sources: string[]) => {
    if (!picturesName || !sources.length) return;

    const current: string[] = getValues(picturesName) ?? [];
    const next = [...current, ...sources.filter((src) => !current.includes(src))];

    setValue(picturesName, next, { shouldDirty: true, shouldValidate: true });
  };

  // The listener below is registered once; this keeps it on the current render's field names.
  const addPicturesRef = useRef(addPictures);
  addPicturesRef.current = addPictures;

  /*
    A picture pasted with no field in focus - the sender clicked the SMS toggle, or blank space in
    the window, then pasted - lands here too, as if pasted into the box. Otherwise it went nowhere,
    or, since the browser hands a paste to wherever the page's selection was left, into the email
    Body hidden behind this view. Caught on the document, ahead of that editor's own handling, and
    only while this is the SMS editor on screen in the window on top; a paste into another field
    there (To, Subject) is that field's.
  */
  useEffect(() => {
    if (!picturesName) return undefined;

    const onDocumentPaste = (event: ClipboardEvent) => {
      const box = boxRef.current;
      if (!box || !isShown(box)) return;

      // Another window open over this one (a confirmation, the signature editor) has the paste.
      const dialog = box.closest('.MuiDialog-root');
      const dialogs = document.querySelectorAll('.MuiDialog-root');
      if (dialog && dialogs[dialogs.length - 1] !== dialog) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target && box.contains(target)) return; // The box's own onPaste has it.
      if (target && dialog?.contains(target) && isShownField(target)) return;

      const capture = capturePastedPictures(event.clipboardData);
      if (!capture) return;

      event.preventDefault();
      event.stopPropagation();

      capture().then(({ pictures, unpasted }) => {
        addPicturesRef.current(pictures.map((picture) => picture.src));
        reportUnpasted(unpasted);
      });
    };

    document.addEventListener('paste', onDocumentPaste, true);
    return () => document.removeEventListener('paste', onDocumentPaste, true);
  }, [picturesName]);

  const onPaste = (event: ReactClipboardEvent<HTMLDivElement>) => {
    if (!picturesName) return;

    const capture = capturePastedPictures(event.clipboardData);
    if (!capture) return;

    // Any words in the paste still go into the box; a paste that is only pictures has nothing to
    // put there.
    if (!hasPastedText(event.clipboardData)) event.preventDefault();

    capture().then(({ pictures, unpasted }) => {
      addPictures(pictures.map((picture) => picture.src));
      reportUnpasted(unpasted);
    });
  };

  // Only a drag carrying files is accepted, so dragging words about the box works as it always has.
  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (picturesName && event.dataTransfer.types.includes('Files')) event.preventDefault();
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!picturesName) return;

    const files = Array.from(event.dataTransfer.files).filter(isPictureFile);
    if (!files.length) return;

    // Otherwise the browser opens the dropped file in place of the app.
    event.preventDefault();

    picturesFromFiles(files).then(({ pictures, unpasted }) => {
      addPictures(pictures.map((picture) => picture.src));
      reportUnpasted(unpasted);
    });
  };

  return (
    <div ref={boxRef}>
      <CasRHFMultilineTextField
        name={name}
        label="Body"
        minRows={5}
        helperText={over ? `${count} - too long for a text` : count}
        // Nested under the class rather than set directly: the theme's `div.MuiFormHelperText-root`
        // rule outranks a bare sx class, which left the over-limit count grey.
        FormHelperTextProps={{
          sx: over ? { '&.MuiFormHelperText-root': { color: 'error.main' } } : undefined,
        }}
        onPaste={onPaste}
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
    </div>
  );
}
